import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../models/auth.model';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      setUser: user => {
        set({
          user,
          isAuthenticated: !!user,
          error: null,
        });
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });

        try {
          // Make API call - MSW will intercept this in development
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            throw new Error('Login failed');
          }

          const data = await response.json();
          get().setUser(data.user);
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        // Clear auth state immediately
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        });

        // Clear any stored tokens
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_refresh_token');
          localStorage.removeItem('auth_user');
        } catch {
          // Handle localStorage errors silently
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: loading => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
